import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  TableCellsIcon,
  PencilSquareIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ShieldExclamationIcon,
  SparklesIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import { BILL_STATUS_LABELS } from '../types/billing.types';
import type { BillStatus, Payment, RecordPaymentDto } from '../types/billing.types';
import InvoiceOverviewTab from '../components/invoice-detail/InvoiceOverviewTab';
import InvoiceLineItemsTab from '../components/invoice-detail/InvoiceLineItemsTab';
import InvoiceContentTab from '../components/invoice-detail/InvoiceContentTab';
import InvoicePreviewTab from '../components/invoice-detail/InvoicePreviewTab';
import RevisionReasonModal from '../components/RevisionReasonModal';
import SendInvoiceModal from '../components/SendInvoiceModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import GuidelineViolationPanel from '../components/GuidelineViolationPanel';
import PreBillReviewPanel from '../components/PreBillReviewPanel';

type DetailTab = 'overview' | 'line-items' | 'content' | 'preview' | 'compliance' | 'ai-review';

const STATUS_COLORS: Record<BillStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  in_review: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  revision: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  sent: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  paid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  overdue: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const InvoiceDetailPage: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useAnimatedNavigate();
  const {
    fetchBill,
    transitionBillStatus,
    downloadInvoicePdf,
    exportLedesFile,
    sendInvoice,
    recordPayment,
    getPaymentsForBill,
    loading,
    error,
  } = useBilling();
  const bill = useBillingStore((s) => s.currentBill);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    if (billId) {
      fetchBill(billId).catch(() => {}).finally(() => setInitialLoadDone(true));
    }
  }, [billId]);

  const handleStatusAction = async (
    newStatus: string,
    reason?: string,
    revisionData?: { revision_reason_id?: string; revision_reason_notes?: string }
  ) => {
    if (!billId) return;
    try {
      await transitionBillStatus(billId, newStatus, reason, revisionData);
      await fetchBill(billId);
    } catch {
      // error handled by hook
    }
  };

  const handleRequestRevision = () => {
    setRevisionModalOpen(true);
  };

  const handleRevisionSubmit = (data: { revision_reason_id?: string; revision_reason_notes?: string }) => {
    setRevisionModalOpen(false);
    handleStatusAction('revision', undefined, data);
  };

  const handleDownloadPdf = () => {
    if (!billId) return;
    downloadInvoicePdf(billId).catch(() => {});
  };

  const handleExportLedes = () => {
    if (!billId) return;
    exportLedesFile(billId).catch(() => {});
  };

  const handleSendInvoice = async (data: { recipient_email: string; cc?: string; subject?: string; message?: string }) => {
    if (!billId) return;
    await sendInvoice(billId, data);
  };

  // Load payments for the bill
  const loadPayments = useCallback(async () => {
    if (!billId) return;
    try {
      const result = await getPaymentsForBill(billId);
      setPayments(result.payments || []);
    } catch {
      // Non-critical — payments list just stays empty
    }
  }, [billId, getPaymentsForBill]);

  useEffect(() => {
    if (billId && bill && ['sent', 'overdue', 'paid'].includes(bill.status)) {
      loadPayments();
    }
  }, [billId, bill?.status]);

  const handleRecordPayment = async (dto: RecordPaymentDto) => {
    await recordPayment(dto);
    // Refresh bill and payments after recording
    if (billId) {
      await fetchBill(billId);
      await loadPayments();
    }
  };

  if (!initialLoadDone) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading invoice...</span>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/billing')}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Billing
        </button>
        <p className="text-gray-500 dark:text-gray-400">Invoice not found.</p>
      </div>
    );
  }

  const isEditable = ['draft', 'in_review', 'revision'].includes(bill.status);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel" style={{ viewTransitionName: 'billing-page' }}>
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Revision banner */}
      {bill.status === 'revision' && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <span className="text-orange-600 dark:text-orange-400 font-medium text-sm">Revision #{bill.revision_count}</span>
            {(bill.revision_reason_ref?.label || bill.revision_reason) && (
              <span className="text-sm text-orange-700 dark:text-orange-300">
                &mdash; {bill.revision_reason_ref?.label || bill.revision_reason}
              </span>
            )}
          </div>
          {bill.revision_reason_notes && (
            <p className="text-sm text-orange-700 dark:text-orange-300 mt-1 italic">
              {bill.revision_reason_notes}
            </p>
          )}
          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            This invoice is open for edits. Submit for re-review when changes are complete.
          </p>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <button
            onClick={() => navigate('/billing')}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Billing
          </button>
          <div className="flex items-center gap-3">
            <h1
              className="text-2xl font-bold text-gray-900 dark:text-white"
              style={{ viewTransitionName: `invoice-number-${bill.bill_id}` }}
            >
              {bill.bill_number}
            </h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[bill.status]}`}>
              {BILL_STATUS_LABELS[bill.status]}
            </span>
            {bill.is_final && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                Finalized
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {(bill.matter as any)?.client?.client_name || ''} &mdash; {(bill.matter as any)?.description || (bill.matter as any)?.matter_number || ''}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {bill.status === 'draft' && (
            <button
              onClick={() => handleStatusAction('in_review')}
              className="px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
            >
              Submit for Review
            </button>
          )}
          {bill.status === 'in_review' && (
            <button
              onClick={() => handleStatusAction('approved')}
              className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Approve
            </button>
          )}
          {bill.status === 'approved' && !bill.is_final && (
            <button
              onClick={handleRequestRevision}
              className="px-3 py-1.5 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Request Revision
            </button>
          )}
          {bill.status === 'revision' && (
            <button
              onClick={() => handleStatusAction('in_review')}
              className="px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
            >
              Submit for Re-Review
            </button>
          )}
          <button
            onClick={handleDownloadPdf}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            PDF
          </button>
          <button
            onClick={handleExportLedes}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            LEDES
          </button>
          {bill && ['approved', 'sent'].includes(bill.status) && (
            <button
              onClick={() => setSendModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Send
            </button>
          )}
          {bill && ['sent', 'overdue'].includes(bill.status) && (
            <button
              onClick={() => setPaymentModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <CurrencyDollarIcon className="h-4 w-4" />
              Record Payment
            </button>
          )}
        </div>
      </div>

      {/* Financial summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Subtotal</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
            ${Number(bill.subtotal || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Tax</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
            ${Number(bill.tax_amount || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total</div>
          <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            ${Number(bill.total || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Balance Due</div>
          <div className={`text-lg font-bold mt-1 ${Number(bill.balance) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
            ${Number(bill.balance || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Payment history */}
      {payments.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Payment History ({payments.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {payments.map((p) => (
              <div key={p.payment_id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${
                    p.payment_status === 'completed' ? 'bg-green-500' :
                    p.payment_status === 'reversed' ? 'bg-red-500' :
                    p.payment_status === 'refunded' ? 'bg-orange-500' :
                    'bg-gray-400'
                  }`} />
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      ${Number(p.amount).toFixed(2)}
                      {p.is_trust_payment && (
                        <span className="ml-1.5 text-xs text-lex-accent">(Trust)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {p.payment_method.replace(/_/g, ' ')}
                      {p.reference_number && ` #${p.reference_number}`}
                      {p.check_number && ` Check #${p.check_number}`}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(p.payment_date).toLocaleDateString()}
                  </div>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                    p.payment_status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                    p.payment_status === 'reversed' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                    p.payment_status === 'refunded' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {p.payment_status}
                    {p.reversal_reason && ` (${p.reversal_reason.replace(/_/g, ' ')})`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        {([
          { id: 'overview' as DetailTab, label: 'Overview', icon: DocumentTextIcon },
          { id: 'line-items' as DetailTab, label: 'Line Items', icon: TableCellsIcon },
          { id: 'content' as DetailTab, label: 'Custom Content', icon: PencilSquareIcon },
          { id: 'preview' as DetailTab, label: 'Preview', icon: EyeIcon },
          { id: 'compliance' as DetailTab, label: 'Compliance', icon: ShieldExclamationIcon },
          { id: 'ai-review' as DetailTab, label: 'AI Review', icon: SparklesIcon },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-lex-accent text-lex-accent'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div key={activeTab} className="lex-tab-panel">
        {activeTab === 'overview' && <InvoiceOverviewTab bill={bill} isEditable={isEditable} />}
        {activeTab === 'line-items' && <InvoiceLineItemsTab billId={bill.bill_id} isEditable={isEditable} />}
        {activeTab === 'content' && <InvoiceContentTab bill={bill} isEditable={isEditable} />}
        {activeTab === 'preview' && <InvoicePreviewTab billId={bill.bill_id} />}
        {activeTab === 'compliance' && <GuidelineViolationPanel billId={bill.bill_id} />}
        {activeTab === 'ai-review' && <PreBillReviewPanel billId={bill.bill_id} />}
      </div>

      {/* Revision Reason Modal */}
      <RevisionReasonModal
        open={revisionModalOpen}
        billNumber={bill.bill_number}
        onSubmit={handleRevisionSubmit}
        onCancel={() => setRevisionModalOpen(false)}
      />

      {/* Send Invoice Modal */}
      {sendModalOpen && bill && (
        <SendInvoiceModal
          billNumber={bill.bill_number}
          clientEmail={(bill as any)?.client?.contact_info?.email || ''}
          onSend={handleSendInvoice}
          onClose={() => setSendModalOpen(false)}
        />
      )}

      {/* Record Payment Modal */}
      {bill && (
        <RecordPaymentModal
          open={paymentModalOpen}
          billId={bill.bill_id}
          billNumber={bill.bill_number}
          balanceDue={Number(bill.balance || 0)}
          currency={bill.currency}
          onRecord={handleRecordPayment}
          onClose={() => setPaymentModalOpen(false)}
        />
      )}
    </div>
  );
};

export default InvoiceDetailPage;
